import * as R from 'ramda'
import { useEffect, useMemo, useRef, useState } from 'react'
import { unstable_batchedUpdates as batch } from 'react-dom'
import { useSelector } from 'react-redux'
import debounce from 'lodash.debounce'
import { Hall, InviteLink, InviteLinkUsage } from '@weblite/ts-action/hall'
import { decodeHallPermission } from '@weblite/permission'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { RootState } from '$setup/redux'
import { createUseEntity } from '$func/create-use-entity.react-hook'
import { useAsyncFunction } from '$func/async-function.react-hook'
import {
  getAllInviteLinks,
  getJoinedViaLinkMembers,
  searchInJoinedViaLinkMembers,
} from '$logic/hall/invite-link.service'
import { useSearchedUserByIds } from '$logic/users'
import { getHall } from './hall.service'
import { getRoom, useRoom } from '$logic/room'
import hostConf from '$config/frontend'
import { HallInviteLink } from './types'
import { hallInviteLinkStorage } from './invite-link.storage'
import { MADRESE_TAALI_MOGHAVEMAT_ROOMID } from '$logic/profile'
import { ChannelType } from '$comp/_app/components/leftPanel/components/create-hall'

const getHallsView = (state: RootState): Record<string, Hall> =>
  state.main.halls

const getHallView = (roomId: string) => (state: RootState) =>
  getHallsView(state)[roomId]

export const useHall = createUseEntity<Hall>(getHallView, getHall)

export function useHallSync(roomId: string) {
  return useSelector(getHallView(roomId))
}

export function useHalls() {
  return useSelector(getHallsView)
}

const memoizedSelector = R.memoizeWith(
  hallId => hallId as string,
  hallId => (state: RootState) =>
    (Object.values(state.main.hallInviteLinks) as HallInviteLink[]).filter(
      inviteLink => inviteLink.roomId === hallId,
    ),
)

const loadingInviteLinksHallIds = new Set<string>()
export function useHallInvitationLinks(hallId: string) {
  const unmountedRef = useRef(false)

  const [loading, setLoading] = useState(true)

  const items = useSelector(memoizedSelector(hallId)) as HallInviteLink[]

  useEffect(() => {
    const currentHallId = hallId
    const unMounted = unmountedRef.current

    if (!loadingInviteLinksHallIds.has(currentHallId)) {
      loadingInviteLinksHallIds.add(currentHallId)
      getAllInviteLinks(hallId)
        .then(res => {
          if (res) {
            hallInviteLinkStorage.add(res)
            loadingInviteLinksHallIds.delete(currentHallId)
          }
          if (unMounted) return
          setLoading(false)
        })
        .catch(() => {
          loadingInviteLinksHallIds.delete(currentHallId)
          if (unMounted) return
          setLoading(false)
        })
    }

    return () => {
      unmountedRef.current = true
    }
  }, [hallId])

  return {
    items,
    loading: loading && items?.length === 0,
  }
}

export function useGroupChangeablePermissionsCount(hallId: string) {
  const { entity: hall, loading } = useHall(hallId)
  const decodedPermissions = useMemo(() => {
    if (!hall?.permissions)
      return {
        addMembers: false,
        changeHallInfo: false,
        sendMedia: false,
        sendMessage: false,
        sendWapp: false,
        pinMessage: false,
      }
    const hallPermissions = decodeHallPermission(hall.permissions)
    return R.pick(
      [
        'addMembers',
        'changeHallInfo',
        'sendMedia',
        'sendMessage',
        'sendWapp',
        'pinMessage',
      ],
      hallPermissions,
    )
  }, [hallId, hall?.permissions])

  const permissionsValues = decodedPermissions
    ? R.values(decodedPermissions)
    : []

  const allowedPermissions = R.filter(item => item, permissionsValues)

  return {
    allowedPermissionsCount: allowedPermissions.length,
    permissionsCount: permissionsValues.length,
    loading,
  }
}

export function useJoinedViaLinkMembers(link: InviteLink) {
  const [items, setItems] = useState<InviteLinkUsage[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [hasMoreItemToLoad, setHasMoreItemToLoad] = useState<boolean>(true)
  const [pageToken, setPageToken] = useState<string>()

  const loadMore = async () => {
    const res = await getJoinedViaLinkMembers(link.roomId, link.link, pageToken)
    batch(() => {
      setLoading(false)
      if (!res?.pageToken) setHasMoreItemToLoad(false)
      setPageToken(res?.pageToken)
      setItems([...items, ...(res?.usages || [])])
    })
  }

  return { items, hasMoreItemToLoad, loadMore, loading }
}

const DEBOUNCE_TIME = 700
const debouncedSearchInJoinedViaLinkMembers = debounce(
  searchInJoinedViaLinkMembers,
  DEBOUNCE_TIME,
)

export function useSearchedJoinedViaLinkMembers({
  inviteLink,
  query,
  fetchedItems,
  hasMoreItemToLoad,
}: {
  inviteLink: InviteLink
  query: string
  fetchedItems: InviteLinkUsage[]
  hasMoreItemToLoad: boolean
}) {
  const [items, setItems] = useState<InviteLinkUsage[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (hasMoreItemToLoad) {
      setLoading(true)
      debouncedSearchInJoinedViaLinkMembers(
        inviteLink.roomId,
        inviteLink.link,
        query,
      )?.then(res => {
        setItems(res?.usages || [])
        setLoading(false)
      })
    }
  }, [inviteLink, query, hasMoreItemToLoad])

  const { searchedItems: localResult } = useSearchedUserByIds({
    query,
    userIds: fetchedItems.map(item => item.userId),
  })

  return {
    loading,
    searchedItems: loading
      ? localResult.map(id => fetchedItems.find(item => item.userId === id))
      : items,
  }
}

export const useHallInfo = (isHall: boolean, roomId: string) => {
  if (!isHall) return {}

  return {
    hall: useHall(roomId).entity,
  }
}

export const MOOKEB_TAG = ChannelType.mookeb
export const EDUCATIONAL_CHANNEL_TAG = ChannelType.educational
export const COURSE_VITRINE_TAG = 'course_vitrine'
export const ISSUE_TAG = 'issue'
const ONLINE_CLASS_TAG = 'online_class_integrated'

const isRoomMookeb = async (roomId: string): Promise<boolean | undefined> => {
  const room = await getRoom(roomId)
  if (room?.type !== 'CHANNEL') return false

  const hall = await getHall(roomId)
  return hall?.tags?.includes(MOOKEB_TAG)
}

export function useRoomIsMookeb(roomId: string) {
  const { value, loading } = useAsyncFunction(
    async () => isRoomMookeb(roomId),
    [roomId],
  )

  return { roomIsMookeb: value, loading }
}

export const isRoomEducational = async (
  roomId: string,
): Promise<boolean | undefined> => {
  const room = await getRoom(roomId)
  if (room?.type !== 'CHANNEL') return false

  const hall = await getHall(roomId)
  return hall?.tags?.includes(EDUCATIONAL_CHANNEL_TAG)
}

export const canRoomHaveCourseVitrine = isRoomEducational

export function useRoomIsEducational(roomId: string) {
  const { value, loading } = useAsyncFunction(
    async () => isRoomEducational(roomId),
    [roomId],
  )

  return { roomIsEducational: value, loading }
}

const provideOnlineClassStrategy: 'true' | 'false' | 'tags' =
  hostConf.VITE_PROVIDE_CLASSROOM_STRATEGY

const canRoomMakeOnlineClass = async (roomId: string) => {
  if (provideOnlineClassStrategy === 'tags') {
    const room = await getRoom(roomId)
    if (room?.type !== 'CHANNEL') return false

    const hall = await getHall(roomId)
    return Boolean(hall?.tags?.includes(ONLINE_CLASS_TAG))
  }
  return provideOnlineClassStrategy === 'true'
}

export function useRoomCanMakeOnlineClass(roomId: string) {
  const { value, loading } = useAsyncFunction(
    async () => canRoomMakeOnlineClass(roomId),
    [roomId],
  )

  return { canMakeOnlineClass: value, loading }
}

export function useRoomHasCourseVitrine(roomId: string) {
  const room = useRoom(roomId)

  const { entity: hall } =
    useConditionalHook(room?.type === 'CHANNEL', {
      hook: useHall,
      hookArgs: roomId,
      defaultValue: undefined,
    }) || {}
  return room?.type !== 'CHANNEL'
    ? false
    : Boolean(hall?.tags?.includes(COURSE_VITRINE_TAG))
}

const canRoomHaveChannelVitrine = async (roomId: string) => {
  if (!hostConf.HAS_CHANNEL_VITRINE) return false

  const room = await getRoom(roomId)
  if (room?.type !== 'CHANNEL') return false
  const isEducational = await isRoomEducational(roomId)
  if (isEducational) return false
  return roomId !== MADRESE_TAALI_MOGHAVEMAT_ROOMID
}

export function useRoomHasChannelVitrine(roomId: string) {
  const { value } = useAsyncFunction(
    () => canRoomHaveChannelVitrine(roomId),
    [roomId],
  )
  return !!value
}

export const canRoomHaveIssue = async (roomId: string) => {
  const room = await getRoom(roomId)
  if (room?.type !== 'CHANNEL') return false

  const hall = await getHall(roomId)
  return Boolean(hall?.tags?.includes(ISSUE_TAG))
}

export function useRoomHasIssue(roomId: string) {
  const room = useRoom(roomId)

  const { entity: hall, loading } =
    useConditionalHook(room?.type === 'CHANNEL', {
      hook: useHall,
      hookArgs: roomId,
      defaultValue: undefined,
    }) || {}

  return Boolean(hall?.tags?.includes(ISSUE_TAG))
}
